import { Eye, BarChart3 } from 'lucide-react'
import type { UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'

// Debug Value Node Schema
const debugValueSchema: UniversalNodeSchema = {
  type: 'debugValues',
  displayName: 'Debug Values',
  category: 'debug',
  icon: Eye,
  description: 'Captures multiple input values at a specified time for debugging',
  inputs: [], // Dynamic inputs handled by multi input handler
  outputs: [], // Debug Values displays values on the right, no output needed
  inputHandler: {
    type: 'multi',
    minInputs: 1,
    maxInputs: 15,
  },
  parameters: [
    {
      name: 'debugTime',
      displayName: 'Debug Time',
      type: 'time' as const,
      default: '09:30',
      timePresets: [
        { value: '09:30', label: '9:30' },
        { value: '10:00', label: '10:00' },
        { value: '11:00', label: '11:00' },
        { value: '12:00', label: '12:00' },
        { value: '13:00', label: '13:00' },
        { value: '14:00', label: '14:00' },
        { value: '15:00', label: '15:00' },
        { value: '15:30', label: '15:30' },
        { value: '16:00', label: '16:00' },
      ],
    },
  ],
  face: { renderer: 'debugValues', inputControl: 'captured' },
}

// Debug Log Node Schema - REMOVED per user request

// Debug Chart Node Schema
const debugChartSchema: UniversalNodeSchema = {
  type: 'debugChart',
  displayName: 'Debug Chart',
  category: 'debug',
  icon: BarChart3,
  description: 'Connects to condition, math, or indicator nodes to plot on the chart pane',
  // Placement and focus draw as one Show on candles switch (utils/debug-show-on.ts);
  // `visible` is the eye in its action bar.
  face: { inputControl: 'swatch', showOn: true, chartEye: true },
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: 'Input',
      // Booleans (condition signals) and numbers (indicator / math / utility
      // outputs) can all be plotted on the chart pane.
      acceptedTypes: ['boolean', 'number'],
      // Debug Chart is a pure observation sink: accept ANY value-producing node
      // and let the type check above gate it. A category allowlist here goes
      // stale every time a new value category is added (it previously omitted
      // 'utilities', so Rolling/Time Slice nodes could not be debugged).
      // Strategy (entry/exit) nodes are still excluded by the strategy-only
      // rule in validateNodeConnection.
      acceptedSources: ['*'],
      fieldSelection: {
        options: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [], // Debug Chart enables Chart pane display, no output needed
  parameters: [
    {
      // The face draws this and focusOnPrice as one Show on candles switch, for
      // a line or a condition; the chart legend, the AI and EdgeScript's
      // `placement` still set it by name.
      name: 'placement',
      displayName: 'Show on',
      description:
        'Where a number is drawn. "Automatic" decides from the data: a series ' +
        'whose values sit in the candle price range goes on the candles, ' +
        'otherwise it gets its own pane below the chart (oscillators, ratios). ' +
        '"On candles" and "Below chart" force one or the other. A condition always ' +
        'draws its own lane below the chart; "On candles" also shades the candles, ' +
        'as focusOnPrice does.',
      type: 'select' as const,
      default: 'auto',
      options: [
        { value: 'auto', label: 'Automatic' },
        { value: 'separate', label: 'Below chart' },
        { value: 'overlay', label: 'On candles' },
      ],
    },
    {
      // VIEW-ONLY Show on candles for a boolean condition: when on, the
      // condition ALSO shades the candles with its highlight band, keeping its
      // own lane below the chart. The editor keeps one condition on the
      // candles at a time (utils/debug-show-on.ts onCandlesWrites). Stripped
      // from the execution signature (see use-graph-signature.ts) so flipping
      // it re-paints without a re-run.
      name: 'focusOnPrice',
      displayName: 'Show on candles',
      description:
        'Also shade the candles where this condition is true, keeping its own lane below ' +
        'the chart. One condition at a time: switching one on in the editor switches ' +
        'the others off.',
      type: 'boolean' as const,
      default: false,
    },
    {
      // VIEW-ONLY saved eye (the node's action bar, the chart legend): off
      // keeps the Debug Chart in the strategy but draws nothing, in every chart
      // cell. Stripped from the execution signature (use-graph-signature.ts),
      // so flipping it never re-runs; no row on the face (face.chartEye).
      name: 'visible',
      displayName: 'Show on chart',
      description: 'Off keeps the Debug Chart in the strategy but draws nothing on the chart.',
      type: 'boolean' as const,
      default: true,
      hidden: true,
    },
    // NOTE: the combined boolean lane-strip pane height is persisted as a
    // VIEW-ONLY param `boolStripHeight` on this node's `data.parameters` (set by
    // dragging the strip's top separator — see boolean-lane-strip.ts and the
    // VIEW_ONLY_PARAM_KEYS set in use-graph-signature.ts). It is intentionally
    // NOT declared here so it stays out of the node's params panel: it's driven
    // entirely by the drag handle, and the params store is free-form.
  ],
}

// Register debug schemas with the Universal Node Registry
export function registerDebugSchemas() {
  UniversalNodeRegistry.register(debugValueSchema)
  UniversalNodeRegistry.register(debugChartSchema)

  console.log(
    'Registered 2 Debug Node Schemas:',
    debugValueSchema.displayName,
    debugChartSchema.displayName,
  )
}

// Export schemas for external access
export { debugValueSchema, debugChartSchema }
